import { useParams, useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import {
  ArrowLeft, Pencil, Mail, Phone, MapPin,
  Building2, FileText, Globe, Users, Clock,
} from 'lucide-react'
import { useLaboratorio, useHistorialLaboratorio } from '../hooks/useBdClientes'

export function LaboratorioDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const labId = Number(id)

  const { data: lab, isLoading } = useLaboratorio(labId)
  const { data: historial } = useHistorialLaboratorio(labId, { page: 1, page_size: 5 })

  useDocumentTitle(lab ? `${lab.razon_social} - Laboratorios` : 'Laboratorios')

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    )
  }

  if (!lab) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Laboratorio no encontrado</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/bd-clientes/laboratorios')}>
          Volver al listado
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/bd-clientes/laboratorios/seguimiento')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Building2 className="h-5 w-5 text-teal-500" />
              {lab.razon_social}
            </h1>
            {lab.activo ? (
              <Badge variant="outline" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Activo</Badge>
            ) : (
              <Badge variant="outline" className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Inactivo</Badge>
            )}
          </div>
        </div>
        <Button
          onClick={() => navigate(`/bd-clientes/laboratorios/${lab.id}/editar`)}
          className="bg-teal-800 hover:bg-teal-900 text-white"
        >
          <Pencil className="mr-2 h-4 w-4" />
          Editar
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Información Principal */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Información Principal</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Razón Social</p>
                <p className="font-medium">{lab.razon_social}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Código ISO</p>
                <p className="font-medium">{lab.codigo_iso || '-'}</p>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Identificación ({lab.tipo_identificacion_nombre})</p>
                  <p className="text-sm font-medium">{lab.identificacion}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Tipo de Laboratorio</p>
                  <p className="text-sm font-medium">{lab.tipo_nombre}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Grupo</p>
                  <p className="text-sm font-medium">{lab.grupo_nombre}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">País</p>
                  <p className="text-sm font-medium">{lab.pais_nombre}</p>
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Correo</p>
                  <p className="text-sm">{lab.correo}</p>
                  {lab.correo_alternativo && (
                    <p className="text-xs text-muted-foreground">{lab.correo_alternativo}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Teléfono Contable</p>
                  <p className="text-sm">{lab.telefono_area_contable}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Dirección</p>
                  <p className="text-sm">{lab.direccion}</p>
                </div>
              </div>
            </div>

            {lab.comentario && (
              <>
                <Separator />
                <div>
                  <p className="text-sm text-muted-foreground">Comentario</p>
                  <p className="text-sm mt-1">{lab.comentario}</p>
                </div>
              </>
            )}

            <Separator />

            <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground">
              <div>
                <p>Creado: {new Date(lab.fecha_creacion).toLocaleString('es-EC')}</p>
                {lab.usuario_creacion_nombre && <p>Por: {lab.usuario_creacion_nombre}</p>}
              </div>
              <div>
                <p>Modificado: {new Date(lab.fecha_modificacion).toLocaleString('es-EC')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Historial reciente */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Historial Reciente</CardTitle>
          </CardHeader>
          <CardContent>
            {historial && historial.results.length > 0 ? (
              <div className="space-y-4">
                {historial.results.map((item) => (
                  <div key={item.id} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="rounded-full bg-teal-500/10 p-1.5">
                        <Clock className="h-3 w-3 text-teal-500" />
                      </div>
                      <div className="flex-1 w-px bg-gray-200 dark:bg-gray-700 mt-1" />
                    </div>
                    <div className="flex-1 pb-4">
                      <p className="text-sm font-medium">{item.accion_display}</p>
                      <p className="text-xs text-muted-foreground">{item.descripcion}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(item.fecha_creacion).toLocaleString('es-EC')}
                        {item.usuario_nombre && ` · ${item.usuario_nombre}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                Sin historial registrado
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
